import { z } from "zod";
/**
 * Time tracking: the shapes both sides agree on, and the pure date/minute
 * maths behind the weekly timesheet.
 *
 * A "day" is a `YYYY-MM-DD` key in the person's own time zone and a week runs
 * Monday to Sunday, keyed by its Monday. Everything here works on those keys
 * (via UTC arithmetic, so daylight saving never shifts a day) rather than on
 * instants, which is what lets the server and the browser cut a week the same
 * way.
 */
/** One entry may not exceed a day; a day's entries may not exceed a day either. */
export const MAX_ENTRY_MINUTES = 24 * 60;
/** Furthest back an entry may be logged, so a closed-out period cannot be rewritten. */
export const MAX_BACKDATE_DAYS = 366;
export const TIMESHEET_STATUSES = ["draft", "submitted", "approved", "rejected"];
export const dateKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a YYYY-MM-DD date.");
export const startTimerBody = z.object({
    taskId: z.string().uuid(),
    note: z.string().trim().max(500).optional(),
});
export const createTimeEntryBody = z.object({
    taskId: z.string().uuid(),
    date: dateKey,
    minutes: z.number().int().min(1).max(MAX_ENTRY_MINUTES),
    note: z.string().trim().max(500).optional(),
});
export const patchTimeEntryBody = z
    .object({
    date: dateKey,
    minutes: z.number().int().min(1).max(MAX_ENTRY_MINUTES),
    note: z.string().trim().max(500).nullable(),
})
    .partial();
/* ───────────────────────────── day / week keys ───────────────────────────── */
function parts(key) {
    const [y, m, d] = key.split("-").map(Number);
    return [y, m, d];
}
/** A `YYYY-MM-DD` key that names a real calendar day. */
export function isDateKey(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const [y, m, d] = parts(value);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}
export function addDays(key, days) {
    const [y, m, d] = parts(key);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/** The Monday of the week containing `key`. */
export function weekStartOf(key) {
    const [y, m, d] = parts(key);
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
    return addDays(key, -((weekday + 6) % 7));
}
/** The seven day keys of the week starting `weekStart`. */
export function weekDays(weekStart) {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}
/** Whole days from `a` to `b` (negative when `b` is earlier). */
export function daysBetween(a, b) {
    const [ay, am, ad] = parts(a);
    const [by, bm, bd] = parts(b);
    return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}
/* ───────────────────────────── minutes ───────────────────────────── */
/** Minutes between two instants, rounded to the nearest minute, at least 1 and at most a day. */
export function elapsedMinutes(startedAt, endedAt) {
    const raw = Math.round((endedAt.getTime() - startedAt.getTime()) / 60_000);
    return Math.min(MAX_ENTRY_MINUTES, Math.max(1, raw));
}
/** "7:05" — hours and zero-padded minutes, the way a timesheet cell reads. */
export function formatClock(minutes) {
    const total = Math.max(0, Math.round(minutes));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
/**
 * Reads what someone types into a timesheet cell: `1:30`, `90m`, `1.5h`,
 * `1h 30m`, or a bare number of hours (`2`, `0.25`). Returns whole minutes,
 * or null when it is not a time. Empty and zero both return 0 (clear the cell).
 */
export function parseClock(input) {
    const text = input.trim().toLowerCase();
    if (text === "")
        return 0;
    let minutes;
    let m;
    if ((m = text.match(/^(\d{1,3}):(\d{1,2})$/))) {
        if (Number(m[2]) > 59)
            return null;
        minutes = Number(m[1]) * 60 + Number(m[2]);
    }
    else if ((m = text.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/)) && (m[1] !== undefined || m[2] !== undefined)) {
        minutes = Math.round(Number(m[1] ?? 0) * 60) + Number(m[2] ?? 0);
    }
    else if ((m = text.match(/^\d+(?:\.\d+)?$/))) {
        minutes = Math.round(Number(text) * 60);
    }
    else {
        return null;
    }
    return minutes <= MAX_ENTRY_MINUTES ? minutes : null;
}
/**
 * Folds entries into the task × day grid. Entries outside the week are
 * ignored; rows keep first-seen order by task title so the grid does not
 * reshuffle while someone types.
 */
export function buildGrid(entries, weekStart) {
    const days = weekDays(weekStart);
    const index = new Map(days.map((d, i) => [d, i]));
    const byTask = new Map();
    const dayTotals = days.map(() => 0);
    for (const entry of entries) {
        const col = index.get(entry.date);
        if (col === undefined)
            continue;
        // Time kept from purged tasks has no task id; rows of it group by title.
        const key = entry.taskId ?? `purged:${entry.taskTitle ?? ""}`;
        let row = byTask.get(key);
        if (!row) {
            row = { taskId: entry.taskId, taskTitle: entry.taskTitle, projectCode: entry.projectCode, days: days.map(() => 0), total: 0 };
            byTask.set(key, row);
        }
        row.days[col] += entry.minutes;
        row.total += entry.minutes;
        dayTotals[col] += entry.minutes;
    }
    const rows = [...byTask.values()].sort((a, b) => (a.taskTitle ?? "").localeCompare(b.taskTitle ?? "") || (a.taskId ?? "").localeCompare(b.taskId ?? ""));
    return { rows, dayTotals, total: dayTotals.reduce((a, b) => a + b, 0) };
}
//# sourceMappingURL=time.js.map